import {
  CalendarIcon,
  ChatSupportIcon,
  ClinicLocationIcon,
  InfoIcon,
  UserBillingIcon,
} from "../../../components/Icons";
/* eslint-disable no-unused-vars */
import { Divider, Skeleton, Typography } from "@mui/material";
import { IPetWellnessplanDetails, IPetWithAge, IWellnessPlan, IWellnessPlanDetails }
  from "../../../services/wellness-plan/wellness-plan.interface";
import { MODALS, OVERDUE_STATUSES, WELLNESS_PLAN_STATUSES, WELLNESS_RENEWAL_ENTRY_POINTS }
  from "../../../config/params";
import { ManagePlanStyledContainer, PetAvatarWithPlansStyled, PlanDetailStyledContainer }
  from "./ManagePlanContainer.style";
import { TotalCareRenewalBanner } from "../../../components/RenewalBanners";
import React, { useEffect, useMemo, useState } from "react";
import {
  getPetOverdueStatus,
  getScratchPayOverdueSubtitle,
} from "../../../utils/plan-overdue-status";
import { useCombinedAlertsDataStore, useWellnessPlanRecord, useWellnessRenewalFunnel } from "../../../hooks";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AvatarCustom } from "../../../components";
import { GvSecondaryButtonCustom } from "../../../styles/styledComponents";
import { PAGES_PATH } from "../../../navigation/params";
import PaymentCardList from "../../../components/PaymentCardList/PaymentCardList";
import { ScratchPayPaymentMethod } from "../../../components/ScratchPayPaymentMethod";
import UserProfileNextIcon from "../../../components/Icons/UserProfileNextIcon";
import { fromUnixToFormattedVisitdateStr } from "../../../utils/unix-timestamp";
import { getPlanOverdueText } from "../../../components/WellnessMemberPlanCard/WellnessMemberPlanCard";
import { useCardList } from "../../../services/card-list/card-list.service";
import { useFetchWellnessPlanByPetId } from "../../../services/wellness-plan/wellness-plan.service";
import { useModal } from "../../../context/ModalContext";

const ManagePlanContainer = () => {
  const navigate = useNavigate();
  const { loading } = useCardList();
  const [petDetails, setPetDetails] = useState<IPetWithAge>();
  const [petWellnessDetails, setWellnessPetDetails] = useState<IWellnessPlanDetails>();
  const [wellnessPlan, setWellnessPlan] = useState<IWellnessPlan>();
  const [isScratchpay, setIsScratchpay] = useState<boolean>(false);
  const { openModal } = useModal();
  const [loadingData, setLoadingData] = useState(false);
  const { getWellnessPlanByPetId, data, loading: petDataLoading } = useFetchWellnessPlanByPetId();
  const [searchParams] = useSearchParams();
  const petId = searchParams.get("id");
  const { setWellnessPlanRecord } = useWellnessPlanRecord();
  const { endsSoonWPData } = useCombinedAlertsDataStore();
  const { startRenewalFunnel } = useWellnessRenewalFunnel();
  const currentPetEndsSoonData = endsSoonWPData?.find((p) => p.petId === petId);
  const planOverdueStatus = useMemo(() => {
    if (!data || !petId || !data.isScratchpay) return null;
    return getPetOverdueStatus(
      data.wellnessPlanPaymentStatus,
      data.isScratchpay,
      data.isOverdue,
    );
  }, [data, petId]);

  const isPlanPaused = useMemo(() => {
    if (!data || !petId) return false;
    const status = data.wellnessPlanPaymentStatus?.toLowerCase();
    return status === WELLNESS_PLAN_STATUSES.PAUSED ||
      status === WELLNESS_PLAN_STATUSES.LOCKED;
  }, [data, petId]);

  const overdueBannerClassName = (() => {
    if (planOverdueStatus === OVERDUE_STATUSES.OVERDUE) {
      return "text-red-4 bg-red-3";
    }
    if (planOverdueStatus === OVERDUE_STATUSES.PAUSED) {
      return "text-white bg-red-4";
    }
    return "";
  })();

  const onError = () => {
    navigate(`${PAGES_PATH.PET_CARE}`);
  };

  const onCompleted = (completedData: IPetWellnessplanDetails) => {
    if (!completedData.isSubscriptionActive || completedData.wellnessPlanDetails.length === 0) {
      navigate(`${PAGES_PATH.PET_CARE}?id=${petId}`);
      return;
    }

    setWellnessPetDetails(completedData.wellnessPlanDetails[0]);
    setPetDetails(completedData.pet);
    setWellnessPlan(completedData.wellnessPlan[0]);
    setIsScratchpay(completedData.isScratchpay || false);
  };

  useEffect(()=>{
    if (petId) {
      getWellnessPlanByPetId(petId, onCompleted, onError);
    }
  }, [petId]);

  useEffect(()=>{
    if (loading || petDataLoading) {
      setLoadingData(true);
    } else {
      setLoadingData(false);
    }
  }, [loading, petDataLoading]);

  useEffect(()=>{
    if (petWellnessDetails) {
      setWellnessPlanRecord(petWellnessDetails);
    }

    return ()=>{
      setWellnessPlanRecord(null);
    };
  }, [petWellnessDetails]);

  const handleOpenUpdateCard = () => {
    openModal(MODALS.UPDATE_PAYMENT_METHOD_MODAL);
  };

  const getBillingText = () => {
    if (isScratchpay) return "Scratch Monthly";
    if (!wellnessPlan) return "";
    const isMonthly = wellnessPlan.billingInterval?.toLowerCase() === "month";
    return `$${wellnessPlan.price} per ${isMonthly ? "month" : "year"}`;
  };

  const renderRenewalBanner = () => {
    if (currentPetEndsSoonData) {
      return (
        <TotalCareRenewalBanner
          onCTAClick={() =>
            startRenewalFunnel(
              [currentPetEndsSoonData],
              WELLNESS_RENEWAL_ENTRY_POINTS.MANAGE_PLAN_RENEWAL_BANNER,
            )}
        />
      );
    }

    return null;
  };
  return (
    <div className="flex justify-center flex-1 self-stretch">
      <ManagePlanStyledContainer>

        <PetAvatarWithPlansStyled>
          <AvatarCustom
            image={petDetails?.image}
            name={petDetails?.name}
            width={80}
            height={80}
            isWellnessMember={petDetails?.isWellnessplan && !isPlanPaused}
            isPlanOverdue={petDetails?.isWellnessplan && isPlanPaused}
          />
          {data && <div className="flex flex-col gap-1 items-center">
            <Typography variant="h4">{data.planName ? data.planName : "GoodPet+ Plan"} for
              {" "} <span className="capitalize">{petDetails?.name}</span>
            </Typography>
            {petWellnessDetails ? <Typography variant="subtitle1">Active</Typography> : null}
          </div>}
        </PetAvatarWithPlansStyled>

        {/* ScratchPay Overdue / Paused Status Banner */}
        {!loadingData && planOverdueStatus && (
          <div className="w-full flex flex-col items-center gap-2">
            <div className={`w-full text-center text-sm
              font-semibold rounded py-0.5 ${overdueBannerClassName}`}>
              {getPlanOverdueText(planOverdueStatus)}
            </div>
            <Typography variant="subtitle2"
              className="!text-gray-1 !text-xs text-center">
              {getScratchPayOverdueSubtitle(planOverdueStatus)}
            </Typography>
          </div>
        )}

        {/* Renewal Banner */}
        {!loadingData && renderRenewalBanner()}

        <PlanDetailStyledContainer>
          {/* Location */}
          <div className="detail_section">
            <div>
              <ClinicLocationIcon width={24} height={24} color="#262626"/>
            </div>
            <div className="sub_details_section">
              <h6 className="font-semibold text-xs text-gray-1">Plan Location</h6>
              {loadingData ?
                <>
                  <Skeleton animation="wave" width={"11rem"}/>
                  <Skeleton animation="wave" width={"15rem"}/>
                </> :
                petWellnessDetails?.location && <>
                  <h5 className="font-semibold text-sm">{`GoodVets ${petWellnessDetails?.location.locationName}`}</h5>
                  <h6 className="text-xs">{petWellnessDetails?.location.address}</h6>
                </>

              }
            </div>
          </div>

          <Divider className="w-full !m-0" />

          {/* Plan */}

          <div className="detail_section">
            <div>
              <CalendarIcon width={24} height={24} color="#262626"/>
            </div>
            <div className="sub_details_section">
              <h6 className="font-semibold text-xs text-gray-1">
                Plan Ends
              </h6>
              {loadingData?

                <Skeleton animation="wave" width={"11rem"}/> :
                <h5 className="font-semibold text-sm">
                  {petWellnessDetails && fromUnixToFormattedVisitdateStr(+petWellnessDetails?.endDate)}
                </h5>}
            </div>
          </div>
          <Divider className="w-full !m-0" />

          {/* Billing */}

          <div className="detail_section">
            <div>
              <UserBillingIcon/>
            </div>
            <div className="sub_details_section">
              <h6 className="font-semibold text-xs text-gray-1">Billing</h6>
              {loadingData?
                <>
                  <Skeleton animation="wave" width={"11rem"}/>
                  <Skeleton animation="wave" width={"15rem"}/>
                </>:
                wellnessPlan &&
              <>
                <h5 className="font-semibold text-sm">
                  {getBillingText()}
                </h5>
              </>}
            </div>
          </div>

        </PlanDetailStyledContainer>

        {/* Payment Section */}

        <div className="flex flex-col gap-2 w-full">
          <h6 className="text-sm font-semibold">Payment Method</h6>
          {loading ?
            <div className="flex gap-3 w-full">
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="rectangular" width="90%" height={40} />
            </div> :null
          }
          {!loadingData ?
            isScratchpay ? (
              <ScratchPayPaymentMethod />
            ) :
              petWellnessDetails ?
                petWellnessDetails.cardDetails !==null ?
                  <PaymentCardList cardDetailsList={[petWellnessDetails.cardDetails]}
                    disableExpand={true} viewSortedCard={petWellnessDetails?.cardId}/>:
                  <div className="flex gap-1 w-full p-4 cursor-pointer justify-between bg-white
               relative group items-center rounded-lg border border-taupe-50" onClick={handleOpenUpdateCard}>
                    <p className="flex items-center gap-2 ">
                      <span><InfoIcon width={20} height={20} color="#262626"/></span>Card on file not available
                    </p>
                    <div className="flex gap-1">
                      <div className="text-sm font-semibold flex items-center  gap-1 capitalize  relative group">
                  Update
                        <span className="absolute left-0 bottom-[-4px] w-0 h-0.5 bg-yellow-500 transition-all
                   duration-300 group-hover:w-full"></span>
                      </div>
                      <UserProfileNextIcon width={20} height={20} />
                    </div>
                  </div>:
                <div
                  className="flex items-center bg-white p-3
        gap-2 rounded-lg border border-taupe-2"
                >
                  <InfoIcon width={18} height={18} color="#D35633"/>
                  <Typography variant="subtitle1" className="!font-semibold">Payment Method Update Needed</Typography>
                </div>:
            null}
        </div>

        <div className="flex flex-col gap-3 py-4 px-0 w-full items-center">
          <GvSecondaryButtonCustom
            onClick={() => openModal(MODALS.CUSTOMER_SUPPORT_MODAL)}
            className="!bg-transparent"
          >
            <ChatSupportIcon width={24} height={24} />
            <span className="ml-2">Customer Support</span>
          </GvSecondaryButtonCustom>
        </div>

      </ManagePlanStyledContainer>
    </div>
  );
};

export default ManagePlanContainer;
